import { useState, useEffect, useMemo, useCallback, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import Dropdown from '../Dropdown';
import LoginMenu from '../../menus/LoginMenu';
import RegisterMenu from '../../menus/RegisterMenu';
import MapBrowserModal from '../../menus/MapBrowserModal';
import ProjectSettingsModal from '../../menus/ProjectSettingsModal';
import { useAuth } from '../../contexts/AuthContext';
import { useEditor } from '../../contexts/EditorContext';
import { hasPermission, PERMISSIONS } from '../../utils/permissions';
import { MapMeta } from '../../types/map';
import { mapsApi } from '../../services/api';
import './BottomBar.css';

interface BottomBarProps {
  /** Current zoom level (percentage display) */
  zoom: number;
  /** Mouse world position for coordinate display */
  mouseWorldPos: { x: number; y: number } | null;
  /** Map scale (pixels per meter) */
  scale: number;
  /** Scale bar component to render */
  scaleBar?: ReactNode;
  /** Whether we're in editor mode */
  isEditorMode: boolean;
  /** Saving state */
  isSaving?: boolean;
  /** Unsaved changes state */
  hasUnsavedChanges?: boolean;
  /** Whether autosave is enabled */
  autoSaveEnabled?: boolean;
  /** Callback to toggle autosave */
  onAutoSaveToggle?: () => void;
  /** Callback to force save */
  onForceSave?: () => void;
  /** Selected map ID for viewer mode */
  selectedMapId?: string | null;
  /** Callback when selecting a map in viewer mode */
  onSelectMap?: (mapId: string) => void;
  /** Current active layer name (for display) */
  activeLayerName?: string;
  /** Callback when clicking the layer button */
  onLayerClick?: () => void;
}

interface FolderNode {
  name: string;
  path: string;
  children: Map<string, FolderNode>;
  maps: MapMeta[];
}

/**
 * Builds a folder tree structure from a flat list of maps
 */
function buildFolderTree(maps: MapMeta[]): FolderNode {
  const root: FolderNode = {
    name: 'Root',
    path: '',
    children: new Map(),
    maps: []
  };

  for (const map of maps) {
    const folderPath = map.folder || '';

    if (!folderPath) {
      root.maps.push(map);
    } else {
      const parts = folderPath.split('/').filter(p => p.trim());
      let currentNode = root;
      let currentPath = '';

      for (const part of parts) {
        currentPath = currentPath ? `${currentPath}/${part}` : part;

        if (!currentNode.children.has(part)) {
          currentNode.children.set(part, {
            name: part,
            path: currentPath,
            children: new Map(),
            maps: []
          });
        }
        currentNode = currentNode.children.get(part)!;
      }

      currentNode.maps.push(map);
    }
  }

  return root;
}

/**
 * Collects all folder paths from the tree
 */
function collectAllFolders(node: FolderNode, folders: string[] = []): string[] {
  if (node.path) {
    folders.push(node.path);
  }
  for (const child of node.children.values()) {
    collectAllFolders(child, folders);
  }
  return folders;
}

/**
 * Bottom bar component that spans the full width of the screen
 * Contains: coordinates, scale bar, save status, settings, map browser, profile
 */
function BottomBar({
  zoom,
  mouseWorldPos,
  scale,
  scaleBar,
  isEditorMode,
  isSaving = false,
  hasUnsavedChanges = false,
  autoSaveEnabled = true,
  onAutoSaveToggle,
  onForceSave,
  selectedMapId,
  onSelectMap,
  activeLayerName,
  onLayerClick
}: BottomBarProps): JSX.Element {
  const { user, isAuthenticated, isLoading: authLoading, logout } = useAuth();
  const { selectedMap, isEditorMode: contextEditorMode, enterEditorMode, exitEditorMode } = useEditor();
  const [showLogin, setShowLogin] = useState<boolean>(false);
  const [showRegister, setShowRegister] = useState<boolean>(false);
  const [showBrowserModal, setShowBrowserModal] = useState<boolean>(false);
  const [showSettingsModal, setShowSettingsModal] = useState<boolean>(false);

  // Viewer mode map selector state
  const [showViewerMapModal, setShowViewerMapModal] = useState<boolean>(false);
  const [publicMaps, setPublicMaps] = useState<MapMeta[]>([]);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [isLoadingMaps, setIsLoadingMaps] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');

  const canAccessEditor = isAuthenticated && user && hasPermission(user.permissions, PERMISSIONS.MAP_CREATE);

  /**
   * Fetch public maps for viewer mode
   */
  useEffect(() => {
    if (!isEditorMode && !isLoadingMaps && publicMaps.length === 0) {
      setIsLoadingMaps(true);
      mapsApi.list()
        .then(response => {
          setPublicMaps(response.maps.filter(m => m.isPublic));
        })
        .catch(err => {
          console.error('Failed to fetch maps:', err);
        })
        .finally(() => {
          setIsLoadingMaps(false);
        });
    }
  }, [isEditorMode, isLoadingMaps, publicMaps.length]);

  /**
   * Build folder tree from public maps
   */
  const folderTree = useMemo(() => buildFolderTree(publicMaps), [publicMaps]);

  /**
   * Filter maps by search query
   */
  const filteredMaps = useMemo(() => {
    if (!searchQuery.trim()) return publicMaps;
    const query = searchQuery.toLowerCase();
    return publicMaps.filter(
      (map) =>
        map.label.toLowerCase().includes(query) ||
        map.id.toLowerCase().includes(query) ||
        (map.folder && map.folder.toLowerCase().includes(query))
    );
  }, [publicMaps, searchQuery]);

  /**
   * Build filtered folder tree for search results
   */
  const filteredFolderTree = useMemo(() => {
    if (!searchQuery.trim()) return folderTree;
    return buildFolderTree(filteredMaps);
  }, [searchQuery, filteredMaps, folderTree]);

  /**
   * Expand all folders when searching
   */
  useEffect(() => {
    if (searchQuery.trim()) {
      const allFolders = collectAllFolders(filteredFolderTree);
      setExpandedFolders(new Set(allFolders));
    }
  }, [searchQuery, filteredFolderTree]);

  /**
   * Get currently selected map for viewer mode
   */
  const selectedViewerMap = useMemo(() => {
    if (!selectedMapId) return null;
    return publicMaps.find(m => m.id === selectedMapId) || null;
  }, [publicMaps, selectedMapId]);

  /**
   * Handle toggle folder
   */
  const handleToggleFolder = useCallback((path: string): void => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  }, []);

  /**
   * Handle viewer map selection
   */
  const handleViewerSelectMap = useCallback((mapId: string): void => {
    onSelectMap?.(mapId);
    setShowViewerMapModal(false);
  }, [onSelectMap]);

  /**
   * Handle escape key for viewer modal
   */
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && showViewerMapModal) {
        e.stopImmediatePropagation();
        setShowViewerMapModal(false);
      }
    };

    if (showViewerMapModal) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [showViewerMapModal]);

  const handleSwitchToLogin = (): void => {
    setShowRegister(false);
    setShowLogin(true);
  };

  const handleSwitchToRegister = (): void => {
    setShowLogin(false);
    setShowRegister(true);
  };

  const handleToggleEditorMode = (): void => {
    if (contextEditorMode) {
      exitEditorMode();
    } else {
      enterEditorMode();
    }
  };

  const profileTrigger = (
    <div className="bottom-bar-profile-trigger">
      {authLoading ? (
        <i className="fas fa-spinner fa-spin"></i>
      ) : (
        <>
          <i className={`fas ${isAuthenticated ? 'fa-user' : 'fa-user-slash'}`}></i>
          <span>{isAuthenticated ? user?.username : 'Guest'}</span>
        </>
      )}
    </div>
  );

  /**
   * Render folder tree item for viewer mode
   */
  const renderFolderTreeItem = (node: FolderNode, level: number = 0): JSX.Element => {
    const isExpanded = expandedFolders.has(node.path);
    const indent = level * 16;

    const childFolders = Array.from(node.children.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
    const sortedMaps = [...node.maps].sort((a, b) =>
      a.label.localeCompare(b.label)
    );

    return (
      <div className="viewer-map-tree-item" key={node.path || 'root'}>
        {node.path && (
          <div
            className={`viewer-map-folder ${isExpanded ? 'viewer-map-folder--expanded' : ''}`}
            onClick={() => handleToggleFolder(node.path)}
            style={{ paddingLeft: `${indent + 8}px` }}
          >
            <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'} viewer-map-chevron`}></i>
            <i className={`fas fa-folder${isExpanded ? '-open' : ''} viewer-map-folder-icon`}></i>
            <span className="viewer-map-folder-name">{node.name}</span>
          </div>
        )}

        {(isExpanded || !node.path) && (
          <div className="viewer-map-children">
            {childFolders.map((child) => renderFolderTreeItem(child, node.path ? level + 1 : level))}
            {sortedMaps.map((map) => (
              <div
                key={map.id}
                className={`viewer-map-item ${selectedMapId === map.id ? 'viewer-map-item--selected' : ''}`}
                onClick={() => handleViewerSelectMap(map.id)}
                style={{ paddingLeft: `${(node.path ? indent + 16 : indent) + 8}px` }}
              >
                <i className="fas fa-map viewer-map-icon"></i>
                <span className="viewer-map-label">{map.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  /**
   * Viewer map modal content
   */
  const viewerMapModalContent = (
    <div className="viewer-map-backdrop" onClick={() => setShowViewerMapModal(false)}>
      <div className="viewer-map-modal" onClick={(e) => e.stopPropagation()}>
        <div className="viewer-map-modal-header">
          <span className="viewer-map-modal-title">Maps</span>
          <div className="viewer-map-search">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder="Search maps..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
            />
            {searchQuery && (
              <button
                className="viewer-map-search-clear"
                onClick={() => setSearchQuery('')}
              >
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>
          <button className="viewer-map-modal-close" onClick={() => setShowViewerMapModal(false)}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="viewer-map-modal-content">
          {searchQuery.trim() ? (
            <div className="viewer-map-search-results">
              {filteredMaps.length === 0 ? (
                <div className="viewer-map-empty">
                  <i className="fas fa-search"></i>
                  <span>No maps found</span>
                </div>
              ) : (
                filteredMaps
                  .sort((a, b) => a.label.localeCompare(b.label))
                  .map((map) => (
                    <div
                      key={map.id}
                      className={`viewer-map-item ${selectedMapId === map.id ? 'viewer-map-item--selected' : ''}`}
                      onClick={() => handleViewerSelectMap(map.id)}
                      style={{ paddingLeft: '1rem' }}
                    >
                      <i className="fas fa-map viewer-map-icon"></i>
                      <span className="viewer-map-label">{map.label}</span>
                      {map.folder && (
                        <span className="viewer-map-folder-path">{map.folder}</span>
                      )}
                    </div>
                  ))
              )}
            </div>
          ) : (
            <div className="viewer-map-tree">
              {renderFolderTreeItem(folderTree)}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className="bottom-bar">
        {/* Left section: Coordinates and Scale */}
        <div className="bottom-bar-left">
          <div className="bottom-bar-coords">
            <span className="bottom-bar-zoom">{Math.round(zoom * 100)}%</span>
          </div>
          <div className="bottom-bar-coords">
            {mouseWorldPos ? (
              <span className="bottom-bar-position">
                X: {Math.round(mouseWorldPos.x / scale)}m Y: {Math.round(mouseWorldPos.y / scale)}m
              </span>
            ) : (
              <span className="bottom-bar-position">X: - Y: -</span>
            )}
          </div>
          {scaleBar && <div className="bottom-bar-scale">{scaleBar}</div>}
        </div>


        {/* Right section: Save controls, Settings, Map Browser, Profile */}
        <div className="bottom-bar-right">
          {isEditorMode ? (
            <>
              <button
                className={`bottom-bar-btn ${autoSaveEnabled ? 'bottom-bar-btn--active' : ''}`}
                onClick={onAutoSaveToggle}
                title={autoSaveEnabled ? 'Autosave enabled' : 'Autosave disabled'}
              >
                <i className="fas fa-clock"></i>
              </button>
              <button
                className={`bottom-bar-btn bottom-bar-btn-save ${hasUnsavedChanges ? 'bottom-bar-btn-save--unsaved' : ''} ${isSaving ? 'bottom-bar-btn-save--saving' : ''}`}
                onClick={onForceSave}
                disabled={isSaving || !hasUnsavedChanges}
                title={isSaving ? 'Saving...' : hasUnsavedChanges ? 'Save changes (Ctrl+S)' : 'All changes saved'}
              >
                <i className={`fas ${isSaving ? 'fa-spinner fa-spin' : 'fa-save'}`}></i>
              </button>
              <button
                className="bottom-bar-btn"
                onClick={() => setShowSettingsModal(true)}
                title="Project settings"
              >
                <i className="fas fa-cog"></i>
              </button>
              {onLayerClick && (
                <button
                  className="bottom-bar-map-btn"
                  onClick={onLayerClick}
                  title="Layers"
                >
                  <i className="fas fa-layer-group"></i>
                  <span className="bottom-bar-map-label">
                    {activeLayerName || 'Default'}
                  </span>
                  <i className="fas fa-chevron-up"></i>
                </button>
              )}
              <button
                className="bottom-bar-map-btn"
                onClick={() => setShowBrowserModal(true)}
                title="Browse maps"
              >
                <i className="fas fa-map"></i>
                <span className="bottom-bar-map-label">
                  {selectedMap ? selectedMap.label : 'Select Map'}
                </span>
                <i className="fas fa-chevron-up"></i>
              </button>
            </>
          ) : (
            <>
              {onLayerClick && (
                <button
                  className="bottom-bar-map-btn"
                  onClick={onLayerClick}
                  title="Layers"
                >
                  <i className="fas fa-layer-group"></i>
                  <span className="bottom-bar-map-label">
                    {activeLayerName || 'Default'}
                  </span>
                  <i className="fas fa-chevron-up"></i>
                </button>
              )}
              {publicMaps.length > 0 && (
                <button
                  className="bottom-bar-map-btn"
                  onClick={() => setShowViewerMapModal(true)}
                  title="Browse maps"
                >
                  <i className="fas fa-map"></i>
                  <span className="bottom-bar-map-label">
                    {selectedViewerMap ? selectedViewerMap.label : 'Select Map'}
                  </span>
                  <i className="fas fa-chevron-up"></i>
                </button>
              )}
            </>
          )}

          <div className="bottom-bar-profile">
            <Dropdown trigger={profileTrigger} align="right">
              {isAuthenticated ? (
                <>
                  <div className="dropdown-item dropdown-item--info">
                    Logged in as <strong>{user?.username}</strong>
                  </div>
                  <div className="dropdown-divider"></div>
                  {canAccessEditor && (
                    <button className="dropdown-item" onClick={handleToggleEditorMode}>
                      <i className={`fas ${contextEditorMode ? 'fa-eye' : 'fa-edit'}`}></i>
                      {contextEditorMode ? 'Exit Editor' : 'Editor Mode'}
                    </button>
                  )}
                  <button className="dropdown-item" onClick={logout}>
                    <i className="fas fa-sign-out-alt"></i> Logout
                  </button>
                </>
              ) : (
                <>
                  <button className="dropdown-item" onClick={() => setShowLogin(true)}>
                    <i className="fas fa-sign-in-alt"></i> Login
                  </button>
                  <button className="dropdown-item" onClick={() => setShowRegister(true)}>
                    <i className="fas fa-user-plus"></i> Register
                  </button>
                </>
              )}
            </Dropdown>
          </div>
        </div>
      </div>

      {/* Modals */}
      <LoginMenu
        isOpen={showLogin}
        onClose={() => setShowLogin(false)}
        onSwitchToRegister={handleSwitchToRegister}
      />
      <RegisterMenu
        isOpen={showRegister}
        onClose={() => setShowRegister(false)}
        onSwitchToLogin={handleSwitchToLogin}
      />
      <MapBrowserModal
        isOpen={showBrowserModal}
        onClose={() => setShowBrowserModal(false)}
      />
      <ProjectSettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
      />

      {/* Viewer mode map modal */}
      {showViewerMapModal && createPortal(viewerMapModalContent, document.body)}
    </>
  );
}

export default BottomBar;
